import { useState, useEffect } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  CustomerFormCustomer,
  CustomerFormData,
  ContactPerson,
  HospProperty,
} from './types';
import { createEmptyFormData } from './types';

interface ExpandedSections {
  contact: boolean;
  address: boolean;
  financial: boolean;
  hospitality: boolean;
}

interface UseCustomerFormDataOptions {
  supabase: SupabaseClient;
  initialData: CustomerFormCustomer | null;
  defaultCountry: string;
  expandedSections: ExpandedSections;
}

export function useCustomerFormData({
  supabase,
  initialData,
  defaultCountry,
  expandedSections,
}: UseCustomerFormDataOptions) {
  // Form data - canonical structure matching Zoho API
  const [formData, setFormData] = useState<CustomerFormData>(createEmptyFormData(defaultCountry));

  // Store original contact_persons to preserve contact_person_id on updates
  const [originalContactPersons, setOriginalContactPersons] = useState<ContactPerson[]>([]);

  // Selected existing customer (when editing or loading from search)
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerFormCustomer | null>(null);

  // Auto-generate display name flag
  const [displayNameManual, setDisplayNameManual] = useState(false);

  // Peace of Sleep properties (loaded when hospitality section expanded)
  const [customerProperties, setCustomerProperties] = useState<HospProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // ==========================================================================
  // LOAD CUSTOMER DATA
  // ==========================================================================

  function loadCustomerData(customer: CustomerFormCustomer) {
    // Parse contact_persons if it's a string
    let rawContactPersons: unknown = customer.contact_persons || [];
    if (typeof rawContactPersons === 'string') {
      try {
        rawContactPersons = JSON.parse(rawContactPersons);
      } catch {
        rawContactPersons = [];
      }
    }
    const contactPersons: ContactPerson[] = Array.isArray(rawContactPersons) ? rawContactPersons as ContactPerson[] : [];

    // Store original contact_persons to preserve contact_person_id on updates
    setOriginalContactPersons(contactPersons);

    // Extract PRIMARY and SECONDARY contacts from contact_persons
    const customerEmail = customer.email?.toLowerCase();

    const primaryContact = contactPersons.find((cp) => cp.is_primary_contact) ||
                           contactPersons.find((cp) => cp.email?.toLowerCase() === customerEmail) ||
                           contactPersons[0] || {};

    const secondaryContact = contactPersons.find((cp) =>
      cp !== primaryContact &&
      (cp.first_name || cp.last_name || cp.email)
    ) || {} as ContactPerson;

    let customerFirstName = '';
    let customerLastName = '';
    if (primaryContact.first_name || primaryContact.last_name) {
      customerFirstName = primaryContact.first_name || '';
      customerLastName = primaryContact.last_name || '';
    } else if (customer.first_name || customer.last_name) {
      customerFirstName = customer.first_name || '';
      customerLastName = customer.last_name || '';
    } else if (customer.customer_type === 'individual' && customer.customer_name) {
      const nameParts = customer.customer_name.split(' ');
      customerFirstName = nameParts[0] || '';
      customerLastName = nameParts.slice(1).join(' ') || '';
    }

    const customerPhone = primaryContact.phone || customer.phone || '';
    const customerMobile = primaryContact.mobile || customer.mobile || '';

    setFormData({
      customer_type: customer.customer_type || 'individual',
      status: customer.status || 'active',
      customer_name: customer.customer_name || '',
      company_name: customer.company_name || '',
      first_name: customerFirstName,
      last_name: customerLastName,
      email: customer.email || '',
      phone: customerPhone,
      mobile: customerMobile,
      website: customer.website || '',
      contact_first_name: secondaryContact.first_name || '',
      contact_last_name: secondaryContact.last_name || '',
      contact_email: (secondaryContact.email && secondaryContact.email.toLowerCase() !== customerEmail)
        ? secondaryContact.email : '',
      contact_phone: secondaryContact.phone || '',
      contact_mobile: secondaryContact.mobile || '',
      contact_designation: secondaryContact.designation || '',
      contact_department: secondaryContact.department || '',
      billing_address: {
        attention: customer.billing_address?.attention || '',
        address: customer.billing_address?.address || '',
        street2: customer.billing_address?.street2 || '',
        suburb: customer.billing_address?.suburb || '',
        city: customer.billing_address?.city || '',
        state: customer.billing_address?.state || '',
        zip: customer.billing_address?.zip || '',
        country: customer.billing_address?.country || defaultCountry,
      },
      shipping_address: {
        attention: customer.shipping_address?.attention || '',
        address: customer.shipping_address?.address || '',
        street2: customer.shipping_address?.street2 || '',
        suburb: customer.shipping_address?.suburb || '',
        city: customer.shipping_address?.city || '',
        state: customer.shipping_address?.state || '',
        zip: customer.shipping_address?.zip || '',
        country: customer.shipping_address?.country || defaultCountry,
      },
      same_shipping: !(
        customer.shipping_address?.address ||
        customer.shipping_address?.city ||
        customer.shipping_address?.attention
      ) || (
        customer.billing_address?.address === customer.shipping_address?.address &&
        customer.billing_address?.city === customer.shipping_address?.city &&
        customer.billing_address?.zip === customer.shipping_address?.zip
      ),
      payment_terms: customer.payment_terms || 0,
      credit_limit: customer.credit_limit || '',
      vat_reg_no: customer.vat_reg_no || '',
      vat_treatment: customer.vat_treatment || '',
      is_taxable: customer.is_taxable !== false,
      is_pos: customer.is_pos || false,
      hospitality_notes: customer.hospitality_notes || '',
      notes: customer.notes || '',
    });
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  useEffect(() => {
    if (initialData) {
      loadCustomerData(initialData);
      // Set as "selected customer" if it's an existing customer (has id)
      if (initialData.id) {
        setSelectedCustomer(initialData);
        setDisplayNameManual(true); // Don't auto-generate for existing customers
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // ==========================================================================
  // AUTO-GENERATE DISPLAY NAME
  // ==========================================================================

  useEffect(() => {
    if (displayNameManual) return;

    let autoName = '';
    if (formData.customer_type === 'business' && formData.company_name) {
      autoName = formData.company_name;
    } else if (formData.customer_type === 'individual') {
      const first = formData.first_name.trim();
      const last = formData.last_name.trim();
      autoName = `${first} ${last}`.trim();
    }

    if (autoName && autoName !== formData.customer_name) {
      setFormData(prev => ({ ...prev, customer_name: autoName }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.customer_type,
    formData.company_name,
    formData.first_name,
    formData.last_name,
    displayNameManual,
  ]);

  // ==========================================================================
  // LOAD CUSTOMER PROPERTIES (Peace of Sleep)
  // ==========================================================================

  async function loadCustomerProperties(customerId: string) {
    if (!customerId) {
      setCustomerProperties([]);
      return;
    }

    setLoadingProperties(true);
    try {
      const { data: properties, error } = await supabase
        .from('hosp_properties')
        .select('id, property_name, total_rooms, total_beds, hospitality_tier, default_service_frequency_days, contract_end_date, contract_status')
        .eq('customer_id', customerId)
        .eq('status', 'active')
        .order('property_name');

      if (error) throw error;
      setCustomerProperties(properties || []);
    } catch (err) {
      console.error('Error loading properties:', err);
      setCustomerProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  }

  useEffect(() => {
    if (expandedSections.hospitality && formData.is_pos && selectedCustomer?.id) {
      loadCustomerProperties(selectedCustomer.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSections.hospitality, formData.is_pos, selectedCustomer?.id]);

  // ==========================================================================
  // CLEAR SELECTION
  // ==========================================================================

  function clearSelection() {
    setSelectedCustomer(null);
    setDisplayNameManual(false);
    setFormData(createEmptyFormData(defaultCountry));
  }

  return {
    formData,
    setFormData,
    selectedCustomer,
    setSelectedCustomer,
    displayNameManual,
    setDisplayNameManual,
    originalContactPersons,
    customerProperties,
    loadingProperties,
    loadCustomerData,
    clearSelection,
  };
}
